import {
  addHours,
  differenceInHours,
  isAfter,
  isBefore,
  subHours,
} from 'date-fns';
import { format, toZonedTime } from 'date-fns-tz';
import { Image } from 'expo-image';
import React from 'react';
import { View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { Reservation } from '@/api/reservations/types';
import {
  formatNumber,
  getCarBrandLogo,
  getCarImages,
  snakeToTitleCase,
} from '@/core';
import { Button, Card, Text } from '@/ui';
import { Chip, ChipType } from '@/ui/chip';
import { Icon } from '@/ui/icon';
import { Dot } from '@/ui/icons/dot';
import { Location } from '@/ui/icons/location';

import { Car } from '../icons';
import { ArrowClockwise } from '../icons/arrow-clockwise';

interface TripCardProps {
  trip: Reservation;
  onPress: () => void;
  showChip?: boolean;
  // Set for an additional driver who still needs to verify.
  invitedBy?: string;
}

export default function TripCard({
  trip,
  onPress,
  showChip = true,
  invitedBy,
}: TripCardProps) {
  const { styles, theme } = useStyles(stylesheet);

  const headerDetails = React.useMemo(() => {
    if (trip.customer.tripsInCollection.length > 0) {
      return {
        chip: {
          type: ChipType.ERROR,
          text: 'Outstanding balance',
        },
      };
    }

    if (trip.status === 'completed') {
      return {
        text: 'Trip Complete',
      };
    }

    if (trip.status === 'billable') {
      return {
        chip: {
          type: ChipType.PENDING,
          text: 'Receipt in progress',
        },
      };
    }

    if (trip.status === 'cleaning') {
      return {
        chip: {
          type: ChipType.PENDING,
          text: 'Receipt in progress',
        },
      };
    }

    if (trip.status === 'rental' && !trip.isCheckinComplete) {
      return {
        chip: {
          type: ChipType.PENDING,
          text: 'Complete check-in to start your trip',
        },
      };
    }

    if (
      trip.status === 'rental' &&
      isAfter(new Date(), subHours(new Date(trip.endTime), 3))
    ) {
      return {
        chip: {
          type: ChipType.PENDING,
          text: 'Complete Drop-off after your trip.',
        },
      };
    }

    if (
      trip.status === 'rental' &&
      trip.isCheckinComplete &&
      trip.preTripCarImages.length === 0 &&
      isAfter(new Date(), new Date(trip.startTime)) &&
      isBefore(new Date(), addHours(new Date(trip.startTime), 6))
    ) {
      return {
        chip: {
          type: ChipType.WARNING,
          text: 'Upload photos of the car before you start your trip',
        },
      };
    }

    if (trip.status === 'rental' && trip.isCheckinComplete) {
      return {
        text: 'Enjoy Your Trip!',
      };
    }

    if (trip.status === 'confirmed') {
      return {
        chip: {
          type: ChipType.PENDING,
          text: 'Check-in will be open 1 hour before your trip',
        },
      };
    }

    if (
      trip.status === 'verified' &&
      differenceInHours(new Date(trip.startTime), new Date()) < 24 &&
      trip.payments.find((p) => p.isHold)
    ) {
      return {
        chip: {
          type: ChipType.PENDING,
          text: 'Almost Done',
        },
      };
    }

    if (
      trip.status === 'verified' &&
      trip.holdAmount > 0 &&
      differenceInHours(new Date(trip.startTime), new Date()) < 24
    ) {
      return {
        chip: {
          type: ChipType.ERROR,
          text: 'Authorize Deposit',
        },
      };
    }

    if (
      trip.status === 'verified' &&
      differenceInHours(new Date(trip.startTime), new Date()) >= 24
    ) {
      return {
        chip: {
          type: ChipType.PENDING,
          text: 'Check-in will be open 1 hour before your trip',
        },
      };
    }

    if (
      trip.status === 'open' &&
      ((trip.customer.insuranceNeeded &&
        trip.customer.insuranceStatus === 'approved') ||
        !trip.customer.insuranceNeeded) &&
      trip.customer.driversLicenseStatus === 'approved' &&
      (!trip.customer.paymentVerificationStatus ||
        trip.customer.paymentVerificationStatus === 'approved') &&
      trip.customer.backgroundCheckStatus !== 'approved'
    ) {
      return {
        chip: {
          type: ChipType.PENDING,
          text: 'Under review',
        },
      };
    }

    if (trip.status === 'open') {
      return {
        chip: {
          type: ChipType.WARNING,
          text: 'Verification needed',
        },
      };
    }
  }, [
    trip.status,
    trip.customer,
    trip.car,
    trip.startTime,
    trip.endTime,
    trip.isCheckinComplete,
    trip.payments,
    trip.preTripCarImages,
  ]);

  const subscriptionProgressBarPercentage = React.useMemo(() => {
    if (!trip) return 0;

    const milesDriven = trip.endingMileage - trip.startingMileage;

    return milesDriven / trip.mileagePlan.totalMilesAllowance;
  }, [trip]);

  const showSubscriptionProgressBar = React.useMemo(() => {
    if (trip.mileagePlan.unlimited) {
      return false;
    }

    if (
      trip.subscription &&
      trip.status === 'rental' &&
      trip.isCheckinComplete
    ) {
      if (trip.mileagePlan.totalMilesAllowance === -1) {
        return false;
      }

      return true;
    }

    return false;
  }, [trip]);

  const showSubscriptionAutopay = React.useMemo(() => {
    if (trip.subscription?.billingCadence === 'daily') {
      return true;
    }

    return false;
  }, [trip]);

  const subscriptionTitle = React.useMemo(() => {
    if (subscriptionProgressBarPercentage >= 1) {
      return 'Exceeded Mileage';
    }

    if (subscriptionProgressBarPercentage >= 0.75) {
      return 'Approaching Plan Limit';
    }

    return 'Miles Driven';
  }, [subscriptionProgressBarPercentage]);

  const subscriptionCaption = React.useMemo(() => {
    if (subscriptionProgressBarPercentage >= 1) {
      return "You've exceeded your plan mileage plan. Contact support to upgrade your mileage plan.";
    }

    if (subscriptionProgressBarPercentage >= 0.75) {
      return `You've reached ${
        subscriptionProgressBarPercentage * 100
      }% of your mileage plan! Additional miles are $0.50/Mile. Contact support to upgrade your mileage plan.`;
    }

    return '';
  }, [subscriptionProgressBarPercentage]);

  const subscriptionProgressBar = React.useMemo(() => {
    return (
      <View
        style={{
          width: '100%',
          height: 8,
          backgroundColor: theme.colors.progressBar.inactive.backgroundColor,
          borderRadius: 8,
        }}
      >
        <View
          style={{
            position: 'absolute',
            width: `${Math.min(subscriptionProgressBarPercentage * 100, 95)}%`,
            height: 8,
            backgroundColor: theme.colors.progressBar.active.backgroundColor,
            borderRadius: 8,
          }}
        />

        <View
          style={{
            position: 'absolute',
            right: 0,
            top: -4,
            width: 16,
            height: 16,
            borderRadius: 16,
            borderWidth: 2,
            borderColor: theme.colors.progressBar.inactive.borderColor,
            backgroundColor: theme.colors.card.default.backgroundColor,
          }}
        />

        <View
          style={{
            position: 'absolute',
            left: `${
              Math.min(subscriptionProgressBarPercentage * 100, 95) - 1
            }%`,
            top: -16,
            width: 36,
            height: 36,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.colors.card.default.backgroundColor,
            borderRadius: 36,
            borderWidth: 2,
            borderColor: theme.colors.card.hover.borderColor,
          }}
        >
          <Icon
            icon={<Car />}
            fillColor={theme.colors.icon.nakedColor}
            type="naked"
            width={16}
            height={16}
          />
        </View>
      </View>
    );
  }, [subscriptionProgressBarPercentage]);

  if (!headerDetails) return null;

  if (trip.subscription) {
    return (
      <View style={styles.container}>
        {showChip && headerDetails.chip ? (
          <View
            style={{
              backgroundColor: theme.colors.background_1,
              borderColor: theme.colors.card.default.borderColor,
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              borderBottomWidth: 1,
              height: 65,
              justifyContent: 'center',
              padding: 16,
            }}
          >
            <Chip
              type={headerDetails.chip.type}
              icon={<Dot />}
              text={headerDetails.chip.text}
              iconWidth={8}
              iconHeight={8}
            />
          </View>
        ) : null}

        <View>
          {showSubscriptionProgressBar ? (
            <View
              style={{
                borderBottomColor: theme.colors.card.default.borderColor,
                borderBottomWidth: 1,
                padding: 16,
                paddingBottom: 24,
              }}
            >
              <View style={{ gap: 24 }}>
                <View>
                  <Text type="bodyBold">{subscriptionTitle}</Text>

                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {trip.endingMileage - trip.startingMileage} miles of{' '}
                    {trip.mileagePlan.totalMilesAllowance} miles
                  </Text>
                </View>

                {subscriptionProgressBar}

                {subscriptionCaption ? (
                  <Text
                    type="caption1"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {subscriptionCaption}
                  </Text>
                ) : null}
              </View>
            </View>
          ) : null}

          {showSubscriptionAutopay ? (
            <View style={{ padding: 16, gap: 16 }}>
              <Card
                style={{
                  backgroundColor: theme.colors.card.hover.backgroundColor,
                  padding: 16,
                  gap: 8,
                }}
              >
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <Icon
                    type="naked"
                    icon={<ArrowClockwise />}
                    fillColor={theme.colors.icon.color}
                    width={10}
                    height={10}
                  />

                  <Text
                    type="caption1"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    Autopay |{' '}
                    {format(
                      toZonedTime(
                        trip.autopayDatetime,
                        trip.pickupLocation.timezone
                      ),
                      'MMM d hh:mm a zzz',
                      { timeZone: trip.pickupLocation.timezone }
                    )}
                  </Text>
                </View>

                <View>
                  <Text type="bodyRegular">Current balance</Text>
                  <Text
                    type="caption1"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    Including charging and tolls
                  </Text>
                </View>

                <View>
                  <Text type="bodyBold">
                    ${formatNumber(trip.outstandingBalance)}
                  </Text>
                </View>
              </Card>
            </View>
          ) : null}

          <View style={{ padding: 16, gap: 16 }}>
            <Chip
              type={ChipType.BRAND}
              icon={<ArrowClockwise />}
              text={`${snakeToTitleCase(
                trip.subscription.renewalPeriod
              )} Subscription`}
              iconWidth={10}
              iconHeight={10}
            />

            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <View
                  style={{
                    backgroundColor: theme.colors.icon.fill,
                    padding: 10,
                    borderRadius: 8,
                    width: 40,
                    height: 40,
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Image
                    style={{
                      width: 18,
                      height: 18,
                    }}
                    source={getCarBrandLogo(trip.car.brandLogos)}
                    contentFit="cover"
                  />
                </View>

                <View>
                  <Text type="bodyBold">{`${snakeToTitleCase(
                    trip.car.brandName
                  )} ${snakeToTitleCase(trip.car.modelName)}`}</Text>
                  <Text
                    type="caption1"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {snakeToTitleCase(trip.car.modelType)}
                  </Text>
                </View>
              </View>

              <View>
                <Image
                  source={{
                    uri:
                      trip.car && getCarImages(trip.car.images).length > 0
                        ? getCarImages(trip.car.images)[0].url
                        : '',
                  }}
                  style={{ width: 120, aspectRatio: 16 / 9 }}
                  contentFit="contain"
                />
              </View>
            </View>

            <View
              style={{
                gap: 8,
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
            >
              <View style={{ flex: 1 }}>
                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Start Time
                </Text>
              </View>

              <Text type="bodyRegular">{format(trip.startTime, 'MMM dd')}</Text>
            </View>

            <View
              style={{
                gap: 8,
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
            >
              <View style={{ flex: 1 }}>
                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Renewal Date
                </Text>
              </View>

              <Text type="bodyRegular">
                {format(trip.subscription.actualExpiryTime, 'MMM dd')}
              </Text>
            </View>

            <View>
              <Button
                type="primary"
                onPress={onPress}
                text="View Subscription Details"
              />
            </View>
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {showChip && headerDetails.chip ? (
        <View
          style={{
            backgroundColor: theme.colors.background_1,
            borderColor: theme.colors.card.default.borderColor,
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            borderBottomWidth: 1,
            height: 65,
            justifyContent: 'center',
            padding: 16,
          }}
        >
          <Chip
            type={headerDetails.chip.type}
            icon={<Dot />}
            text={headerDetails.chip.text}
            iconWidth={8}
            iconHeight={8}
          />
        </View>
      ) : null}

      <View>
        <View
          style={{
            padding: 16,
            borderBottomColor: theme.colors.card.default.borderColor,
            borderBottomWidth: 1,
          }}
        >
          <Text type="title2">
            {invitedBy
              ? `${invitedBy} Invited You To This Trip`
              : headerDetails.text || 'Your Upcoming Trip'}
          </Text>
        </View>

        <View style={{ padding: 16, gap: 16 }}>
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <View
                style={{
                  backgroundColor: theme.colors.icon.fill,
                  padding: 10,
                  borderRadius: 8,
                  width: 40,
                  height: 40,
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Image
                  style={{
                    width: 18,
                    height: 18,
                  }}
                  source={getCarBrandLogo(trip.car.brandLogos)}
                  contentFit="cover"
                />
              </View>

              <View>
                <Text type="bodyBold">{`${snakeToTitleCase(
                  trip.car.brandName
                )} ${snakeToTitleCase(trip.car.modelName)}`}</Text>
                <Text
                  type="caption1"
                  style={{ color: theme.colors.text.secondary }}
                >
                  {snakeToTitleCase(trip.car.modelType)}
                </Text>
              </View>
            </View>

            <View>
              <Image
                source={{
                  uri:
                    trip.car && getCarImages(trip.car.images).length > 0
                      ? getCarImages(trip.car.images)[0].url
                      : '',
                }}
                style={{ width: 120, aspectRatio: 16 / 9 }}
                contentFit="contain"
              />
            </View>
          </View>

          <View style={{ gap: 8 }}>
            <View style={{ gap: 8, flexDirection: 'row' }}>
              <View>
                <Icon
                  type="dressed"
                  icon={<Location />}
                  fillColor={theme.colors.icon.color}
                  width={16}
                  height={16}
                  style={{
                    width: 40,
                    height: 40,
                  }}
                />
              </View>

              <View style={{ flex: 1 }}>
                <Text
                  tx={
                    trip.customPickupLocation && trip.customReturnLocation
                      ? 'carId.deliveryLocation'
                      : 'carId.pickupLocation'
                  }
                  type="bodyBold"
                />

                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.secondary }}
                >
                  {format(
                    toZonedTime(trip.startTime, trip.pickupLocation.timezone),
                    'MMM dd - hh:mm a'
                  )}
                </Text>

                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.secondary }}
                >
                  {trip.customPickupLocation || trip.pickupLocation.address}
                </Text>
              </View>
            </View>

            <View style={{ gap: 8, flexDirection: 'row' }}>
              <View>
                <Icon
                  type="dressed"
                  icon={<Location />}
                  fillColor={theme.colors.icon.color}
                  width={16}
                  height={16}
                  style={{
                    width: 40,
                    height: 40,
                  }}
                />
              </View>

              <View style={{ flex: 1 }}>
                <Text tx="carId.returnLocation" type="bodyBold" />

                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.secondary }}
                >
                  {format(
                    toZonedTime(trip.endTime, trip.returnLocation.timezone),
                    'MMM dd - hh:mm a'
                  )}
                </Text>

                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.secondary }}
                >
                  {trip.customReturnLocation || trip.returnLocation.address}
                </Text>
              </View>
            </View>
          </View>

          <View>
            {invitedBy ? (
              <Button
                type="primary"
                onPress={onPress}
                text="Verify Your Details"
              />
            ) : (
              <Button
                type="primary"
                onPress={onPress}
                tx="tripCard.viewTripDetails"
              />
            )}
          </View>
        </View>
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flexGrow: 1,
    flexDirection: 'column',
    backgroundColor: theme.colors.card.default.backgroundColor,
    borderRadius: 24,
    borderColor: theme.colors.card.default.borderColor,
    borderWidth: 1,
  },
}));
